// REUNIÃO GERAL (2026-09-03) — o rodízio de apresentador.
//
// Uma pessoa por semana apresenta ~15 min. É o bloco mais longo da reunião de
// propósito: 15 min é tempo de aula, não de aviso, e é o que faz a reunião ser
// do time e não do dono.
//
// A escala fica visível para todos com semanas de antecedência — quem está
// escalado precisa de tempo para preparar, e ver o próprio nome no calendário
// é o que cria a preparação. O formato sugerido é sempre o mesmo, em 3 partes
// ("o que é", "por que importa pra gente", "um caso nosso"), justamente para
// tirar a pressão de ter que ser perfeito.

import { useCallback, useEffect, useState } from 'react';
import {
  App as AntApp, Button, DatePicker, Empty, Input, Modal, Space, Table, Tag, Typography,
} from 'antd';
import { CalendarOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api, erroMsg, fmtSemana, isoDia, nomeDoEmail, segundaDe } from './utils';
import { EscalaItem } from './types';

const { Text, Paragraph } = Typography;

interface Props {
  podeGerenciar: boolean;
  semanaAtual: string;
}

export default function EscalaPanel({ podeGerenciar, semanaAtual }: Props) {
  const { message } = AntApp.useApp();
  const [escala, setEscala] = useState<EscalaItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [aberto, setAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const [semana, setSemana] = useState<any>(null);
  const [email, setEmail] = useState('');
  const [tema, setTema] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      // A partir de 8 semanas atrás: dá para ver quem já apresentou e fechar o
      // rodízio sem repetir ninguém cedo demais.
      const de = dayjs(semanaAtual).subtract(8, 'week').format('YYYY-MM-DD');
      const d = await api('op_pauta_escala_listar', { de });
      setEscala(Array.isArray(d?.escala) ? d.escala : []);
    } catch (e: any) {
      message.error('Falha ao carregar o rodízio: ' + erroMsg(e));
    } finally { setCarregando(false); }
  }, [semanaAtual, message]);
  useEffect(() => { carregar(); }, [carregar]);

  function abrir(item?: EscalaItem) {
    setSemana(item ? dayjs(isoDia(item.semana_ref)) : null);
    setEmail(item?.email || '');
    setTema(item?.tema || '');
    setAberto(true);
  }

  async function salvar() {
    if (!semana) { message.warning('Escolha a semana.'); return; }
    if (!email.includes('@')) { message.warning('Informe o e-mail de quem apresenta.'); return; }
    setSalvando(true);
    try {
      // A semana é sempre a SEGUNDA — é a chave da tabela. Se a pessoa clicar
      // numa quarta no calendário, normalizamos aqui.
      await api('op_pauta_escala_definir', {
        semana_ref: segundaDe(semana).format('YYYY-MM-DD'),
        email: email.trim().toLowerCase(),
        tema: tema.trim() || undefined,
      });
      setAberto(false);
      await carregar();
    } catch (e: any) {
      message.error('Falha ao escalar: ' + erroMsg(e));
    } finally { setSalvando(false); }
  }

  const colunas = [
    {
      title: 'Semana', dataIndex: 'semana_ref', key: 'semana', width: 150,
      render: (v: string) => {
        const eh = isoDia(v) === isoDia(semanaAtual);
        return (
          <Space size={6}>
            <Text strong={eh}>{fmtSemana(v)}</Text>
            {eh && <Tag color="blue">esta semana</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Apresenta', key: 'quem',
      render: (_: any, r: EscalaItem) => <Text>{r.nome || nomeDoEmail(r.email)}</Text>,
    },
    {
      title: 'Tema', dataIndex: 'tema', key: 'tema',
      render: (v: string | null) => v
        ? <Text>{v}</Text>
        : <Text type="secondary">a confirmar</Text>,
    },
    ...(podeGerenciar ? [{
      title: '', key: 'acoes', width: 50,
      render: (_: any, r: EscalaItem) => (
        <Button size="small" type="text" icon={<EditOutlined />} onClick={() => abrir(r)} />
      ),
    }] : []),
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Quem apresenta os 15 minutos de tema técnico. O formato é sempre o mesmo, em três
        partes: <Text strong>o que é</Text> · <Text strong>por que importa pra gente</Text> ·{' '}
        <Text strong>um caso nosso</Text>.
      </Paragraph>

      {podeGerenciar && (
        <Button icon={<CalendarOutlined />} onClick={() => abrir()}>Escalar uma semana</Button>
      )}

      {escala.length === 0 && !carregando
        ? <Empty description="Ninguém escalado ainda." />
        : <Table<EscalaItem> rowKey="semana_ref" size="small" columns={colunas}
            dataSource={escala} loading={carregando} pagination={false} tableLayout="fixed" />}

      <Modal
        title="Escalar apresentador"
        open={aberto}
        onCancel={() => setAberto(false)}
        onOk={salvar}
        confirmLoading={salvando}
        okText="Salvar"
        cancelText="Cancelar"
        width="94vw"
        style={{ maxWidth: 560, top: 40 }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong>Semana</Text><br />
            <DatePicker value={semana} onChange={setSemana} format="DD/MM/YYYY"
              placeholder="qualquer dia da semana" style={{ width: 220 }} />
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Vale qualquer dia — guardamos a semana toda.
            </Text>
          </div>
          <div>
            <Text strong>Quem apresenta</Text><br />
            <Input value={email} placeholder="email@hydrodataeng.com.br"
              onChange={(e) => setEmail(e.target.value)} style={{ width: 280 }} />
          </div>
          <div>
            <Text strong>Tema</Text><br />
            <Input value={tema} maxLength={300} placeholder="pode ficar em branco e definir depois"
              onChange={(e) => setTema(e.target.value)} />
          </div>
        </Space>
      </Modal>
    </Space>
  );
}
